import { QualityLevel } from "../core/types.ts";
import {
  GRID_WIDTH,
  GRID_HEIGHT,
  CELL_SIZE,
  POST_FX_RESOLUTION_CAP,
} from "../core/config.ts";
import {
  BLOOM,
  VERTEX_SHADER,
  BLOOM_EXTRACT_FRAG,
  BLOOM_BLUR_FRAG,
  BLOOM_COMPOSITE_FRAG,
  CHROMA_FRAGMENT,
} from "./bloomShaders.ts";

/**
 * Per-instance bloom tuning. The arena instance passes nothing → every field
 * defaults to the shared `BLOOM` constants, so the arena pass is byte-identical
 * to before this config existed (locked by `WebGL2Effects.arena-config.test`).
 *
 * BL-37b/S87: the UI instance is the SAME class + the SAME shaders, but the
 * HUD chrome is small, sharp and static where the arena is big, soft and
 * moving — so the UI overrides the *uniform values* (a higher threshold so only
 * hot edges bloom, a tighter blur so fine chrome stays defined, chroma off so
 * no fringe near text). The GLSL is never touched (no new fragile shader).
 */
export interface BloomConfig {
  /** `u_threshold` for the extract pass. Default `BLOOM.THRESHOLD` (0.5). */
  threshold?: number;
  /** Composite `u_intensity` at the `high` tier. Default `BLOOM.INTENSITY_HIGH` (1.0). */
  intensityHigh?: number;
  /** Composite `u_intensity` at the `medium` tier. Default `BLOOM.INTENSITY_MEDIUM` (0.5). */
  intensityMedium?: number;
  /** `u_offset` for the `high`-tier chroma pass. Default `BLOOM.CHROMA_OFFSET` (0.003). */
  chromaOffset?: number;
  /** Bloom-blur downscale divisor (source ÷ this). Default `BLOOM.DOWNSCALE` (2 = half-res). */
  bloomDownscale?: number;
  /**
   * Maps a source-canvas size → the working (pass/output) resolution. The
   * arena default caps to logical-pixels × `POST_FX_RESOLUTION_CAP` (avoids
   * DPR² fill for a soft overlay). A second instance (the UI) supplies its own
   * mapping (e.g. cap to CSS-logical so a HiDPI HUD doesn't pay DPR² on a
   * dirty-redraw frame).
   */
  resolveResolution?: (sourceW: number, sourceH: number) => {
    w: number;
    h: number;
  };
}

/** Resolve a {@link BloomConfig} to concrete values (arena defaults). */
interface ResolvedBloomConfig {
  threshold: number;
  intensityHigh: number;
  intensityMedium: number;
  chromaOffset: number;
  bloomDownscale: number;
  resolveResolution: (sourceW: number, sourceH: number) => {
    w: number;
    h: number;
  };
}

// Logical game resolution (DPR-independent). The post-FX working resolution is
// capped to this (× POST_FX_RESOLUTION_CAP) so HiDPI backing stores don't pay
// DPR² fill cost for a soft overlay — see POST_FX_RESOLUTION_CAP docs.
const LOGICAL_WIDTH = GRID_WIDTH * CELL_SIZE;
const LOGICAL_HEIGHT = GRID_HEIGHT * CELL_SIZE;

/**
 * Pure resolution-cap math for the post-FX passes. Exported for testing.
 *
 * The working resolution is `logical × cap`, rounded, but never larger than the
 * actual backing store (so a DPR=1 display is never upscaled) and never below
 * 1px. This is the single contract behind the WebGL fill-cost reduction; see
 * POST_FX_RESOLUTION_CAP for the rationale.
 */
export function computePostFxResolution(
  backingW: number,
  backingH: number,
  logicalW: number,
  logicalH: number,
  cap: number,
): { w: number; h: number } {
  const capW = Math.max(1, Math.round(logicalW * cap));
  const capH = Math.max(1, Math.round(logicalH * cap));
  return {
    w: Math.min(backingW, capW),
    h: Math.min(backingH, capH),
  };
}

interface FBO {
  framebuffer: WebGLFramebuffer;
  texture: WebGLTexture;
  width: number;
  height: number;
}

/**
 * WebGL2 post-processing effects layer.
 * Uses FBO ping-pong: Source → Extract+Blur (bloom) → Composite → Chroma.
 *
 * BL-44: this is now a pure ACTUATOR. The per-frame ms window + the
 * degrade/recover decision moved up into the single load brain
 * (`engine/PerfGovernor`), which calls {@link setQuality} on the arena instance.
 * The render math + bloom params are byte-untouched (`arena-config.test`).
 */
export class WebGL2Effects {
  private gl: WebGL2RenderingContext | null = null;
  private canvas: HTMLCanvasElement;
  private quality: QualityLevel = "high";
  private enabled_ = true;
  private bloomExtractProgram: WebGLProgram | null = null;
  private bloomBlurProgram: WebGLProgram | null = null;
  private bloomCompositeProgram: WebGLProgram | null = null;
  private chromaProgram: WebGLProgram | null = null;
  private sourceTexture: WebGLTexture | null = null;
  // Track the allocated source-texture dimensions so per-frame uploads can use
  // texSubImage2D (no storage realloc) instead of texImage2D (full re-spec).
  private sourceTexW = 0;
  private sourceTexH = 0;
  private quadVAO: WebGLVertexArrayObject | null = null;
  private quadVBO: WebGLBuffer | null = null;
  private fboA: FBO | null = null;
  private fboB: FBO | null = null;
  private fboWidth = 0;
  private fboHeight = 0;
  private initialized = false;
  private contextLost = false;
  private uniformCache = new Map<
    WebGLProgram,
    Map<string, WebGLUniformLocation | null>
  >();
  private boundContextLost: ((e: Event) => void) | null = null;
  private boundContextRestored: ((e: Event) => void) | null = null;
  private readonly cfg: ResolvedBloomConfig;

  constructor(canvas: HTMLCanvasElement, config: BloomConfig = {}) {
    this.canvas = canvas;
    this.cfg = {
      threshold: config.threshold ?? BLOOM.THRESHOLD,
      intensityHigh: config.intensityHigh ?? BLOOM.INTENSITY_HIGH,
      intensityMedium: config.intensityMedium ?? BLOOM.INTENSITY_MEDIUM,
      chromaOffset: config.chromaOffset ?? BLOOM.CHROMA_OFFSET,
      bloomDownscale: config.bloomDownscale ?? BLOOM.DOWNSCALE,
      resolveResolution:
        config.resolveResolution ??
        ((sourceW, sourceH) =>
          computePostFxResolution(
            sourceW,
            sourceH,
            LOGICAL_WIDTH,
            LOGICAL_HEIGHT,
            POST_FX_RESOLUTION_CAP,
          )),
    };
    this.listenForContextEvents();
  }

  /** The resolved bloom tuning (test hook — locks the arena defaults). */
  getBloomParams(): Readonly<ResolvedBloomConfig> {
    return this.cfg;
  }

  get enabled(): boolean {
    return this.enabled_;
  }
  set enabled(val: boolean) {
    this.enabled_ = val;
  }

  private listenForContextEvents(): void {
    this.boundContextLost = (e: Event) => {
      e.preventDefault();
      this.contextLost = true;
      this.initialized = false;
      this.uniformCache.clear();
      console.warn("[WebGL2] CONTEXT_LOST — disabling post-processing");
    };
    this.boundContextRestored = () => {
      console.info(
        "[WebGL2] CONTEXT_RESTORED — will re-initialize on next render",
      );
      this.contextLost = false;
      // Don't eagerly re-init here. Old GL state is invalid anyway.
      // initGL() will be called lazily on next render().
      this.initialized = false;
      this.gl = null;
      this.fboA = null;
      this.fboB = null;
      this.fboWidth = 0;
      this.fboHeight = 0;
      this.bloomExtractProgram = null;
      this.bloomBlurProgram = null;
      this.bloomCompositeProgram = null;
      this.chromaProgram = null;
      this.sourceTexture = null;
      this.sourceTexW = 0;
      this.sourceTexH = 0;
      this.quadVAO = null;
      this.quadVBO = null;
    };
    this.canvas.addEventListener("webglcontextlost", this.boundContextLost);
    this.canvas.addEventListener(
      "webglcontextrestored",
      this.boundContextRestored,
    );
  }

  private initGL(): void {
    // Reset ALL GPU resource handles — they're invalid after context loss
    this.fboA = null;
    this.fboB = null;
    this.fboWidth = 0;
    this.fboHeight = 0;
    this.bloomExtractProgram = null;
    this.bloomBlurProgram = null;
    this.bloomCompositeProgram = null;
    this.chromaProgram = null;
    this.sourceTexture = null;
    this.sourceTexW = 0;
    this.sourceTexH = 0;
    this.quadVAO = null;
    this.quadVBO = null;

    const gl = this.canvas.getContext("webgl2", {
      alpha: true,
      premultipliedAlpha: false,
      antialias: false,
      preserveDrawingBuffer: false,
      powerPreference: "default",
    });

    if (!gl) {
      console.warn("WebGL2 not available, effects disabled");
      this.quality = "off";
      return;
    }

    this.gl = gl;
    this.setupShaders();
    this.setupGeometry();
    this.initialized = true;
  }

  setQuality(quality: QualityLevel): void {
    this.quality = quality;
  }

  getQuality(): QualityLevel {
    return this.quality;
  }

  isContextLost(): boolean {
    if (this.contextLost) return true;
    if (this.quality === "off") return true;
    // Not yet initialized = not lost, just lazy — let render() handle init
    if (!this.initialized) return false;
    // Check native GL status in case event hasn't fired yet
    if (this.gl && this.gl.isContextLost()) {
      this.contextLost = true;
      this.initialized = false;
      console.warn(
        "[WebGL2] Native gl.isContextLost() detected — marking lost",
      );
      return true;
    }
    return false;
  }

  getCanvas(): HTMLCanvasElement {
    return this.canvas;
  }

  destroy(): void {
    if (this.boundContextLost)
      this.canvas.removeEventListener(
        "webglcontextlost",
        this.boundContextLost,
      );
    if (this.boundContextRestored)
      this.canvas.removeEventListener(
        "webglcontextrestored",
        this.boundContextRestored,
      );
    this.boundContextLost = null;
    this.boundContextRestored = null;
    if (!this.gl) return;
    const gl = this.gl;
    this.uniformCache.clear();
    if (this.bloomExtractProgram) gl.deleteProgram(this.bloomExtractProgram);
    if (this.bloomBlurProgram) gl.deleteProgram(this.bloomBlurProgram);
    if (this.bloomCompositeProgram)
      gl.deleteProgram(this.bloomCompositeProgram);
    if (this.chromaProgram) gl.deleteProgram(this.chromaProgram);
    if (this.sourceTexture) gl.deleteTexture(this.sourceTexture);
    if (this.quadVAO) gl.deleteVertexArray(this.quadVAO);
    if (this.quadVBO) gl.deleteBuffer(this.quadVBO);
    this.destroyFBOs();
    this.bloomExtractProgram = null;
    this.bloomBlurProgram = null;
    this.bloomCompositeProgram = null;
    this.chromaProgram = null;
    this.sourceTexture = null;
    this.quadVAO = null;
    this.quadVBO = null;
    this.initialized = false;
    this.gl = null;
  }

  render(sourceCanvas: HTMLCanvasElement, chromaBoost = 0): void {
    if (!this.enabled_) return;
    // Lazy init on first render call
    if (!this.initialized && !this.contextLost && this.quality !== "off") {
      this.initGL();
      if (!this.initialized) return;
    }
    if (!this.gl || !this.initialized || this.contextLost) return;
    if (this.quality === "off" || this.quality === "low") return;

    const gl = this.gl;

    // Cap the post-FX working resolution to logical pixels (× cap). On a HiDPI
    // backing store the bloom/composite/chroma passes would otherwise run at
    // DPR² the pixel count for a soft overlay the eye cannot resolve at that
    // density. The source texture is still uploaded at the full canvas
    // resolution (sampled with LINEAR), so input quality is unaffected; only
    // the pass/output resolution shrinks and is upscaled bilinearly on
    // composite. Never upscale beyond the actual backing store (DPR=1 path).
    const { w, h } = this.cfg.resolveResolution(
      sourceCanvas.width,
      sourceCanvas.height,
    );

    // Sync internal WebGL canvas size (always hidden — composited via drawImage,
    // upscaled to the backing store at the call site).
    if (this.canvas.width !== w || this.canvas.height !== h) {
      this.canvas.width = w;
      this.canvas.height = h;
    }

    // Clear to fully transparent before rendering — prevents feedback accumulation
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, w, h);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);

    // Disable blending for all FBO passes (they fully overwrite target)
    gl.disable(gl.BLEND);

    // Ensure FBOs match canvas dimensions (half-res for bloom per Q-020; the
    // UI instance tightens this to full-res so fine chrome stays defined).
    const bloomW = Math.max(1, (w / this.cfg.bloomDownscale) | 0);
    const bloomH = Math.max(1, (h / this.cfg.bloomDownscale) | 0);
    this.ensureFBOs(bloomW, bloomH);

    // Upload source canvas as texture
    this.updateSourceTexture(sourceCanvas);

    if (this.quality === "high" || this.quality === "medium") {
      // Pass 1: Extract bright pixels → FBO_A (half-res)
      this.renderToFBO(
        this.fboA!,
        this.bloomExtractProgram!,
        (prog) => {
          gl.activeTexture(gl.TEXTURE0);
          gl.bindTexture(gl.TEXTURE_2D, this.sourceTexture);
          gl.uniform1i(this.getUniform(prog, "u_texture"), 0);
          gl.uniform1f(
            this.getUniform(prog, "u_threshold"),
            this.cfg.threshold,
          );
        },
        bloomW,
        bloomH,
      );

      // Pass 2: Blur FBO_A → FBO_B (separable Gaussian horizontal)
      this.renderToFBO(
        this.fboB!,
        this.bloomBlurProgram!,
        (prog) => {
          gl.activeTexture(gl.TEXTURE0);
          gl.bindTexture(gl.TEXTURE_2D, this.fboA!.texture);
          gl.uniform1i(this.getUniform(prog, "u_texture"), 0);
          gl.uniform2f(this.getUniform(prog, "u_direction"), 1.0 / bloomW, 0.0);
        },
        bloomW,
        bloomH,
      );

      // Pass 3: Blur FBO_B → FBO_A (separable Gaussian vertical)
      this.renderToFBO(
        this.fboA!,
        this.bloomBlurProgram!,
        (prog) => {
          gl.activeTexture(gl.TEXTURE0);
          gl.bindTexture(gl.TEXTURE_2D, this.fboB!.texture);
          gl.uniform1i(this.getUniform(prog, "u_texture"), 0);
          gl.uniform2f(this.getUniform(prog, "u_direction"), 0.0, 1.0 / bloomH);
        },
        bloomW,
        bloomH,
      );

      // Pass 4: Composite source + bloom → FBO_B (full-res)
      this.renderToFBO(
        this.fboB!,
        this.bloomCompositeProgram!,
        (prog) => {
          gl.activeTexture(gl.TEXTURE0);
          gl.bindTexture(gl.TEXTURE_2D, this.sourceTexture);
          gl.uniform1i(this.getUniform(prog, "u_source"), 0);
          gl.activeTexture(gl.TEXTURE1);
          gl.bindTexture(gl.TEXTURE_2D, this.fboA!.texture);
          gl.uniform1i(this.getUniform(prog, "u_bloom"), 1);
          gl.uniform1f(
            this.getUniform(prog, "u_intensity"),
            this.quality === "high"
              ? this.cfg.intensityHigh
              : this.cfg.intensityMedium,
          );
        },
        w,
        h,
      );
    }

    // Pass 5: Chromatic aberration → screen (reads from composite FBO or source)
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, w, h);
    if (this.quality === "high" && this.chromaProgram) {
      gl.useProgram(this.chromaProgram);
      gl.bindVertexArray(this.quadVAO);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, this.fboB!.texture);
      gl.uniform1i(this.getUniform(this.chromaProgram, "u_texture"), 0);
      gl.uniform1f(
        this.getUniform(this.chromaProgram, "u_offset"),
        // BL-239: base arena chroma + the transient hero-moment glitch burst
        // (0 when idle → byte-identical to before).
        this.cfg.chromaOffset + chromaBoost,
      );
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    } else if (this.quality === "medium") {
      // Medium: just blit composite (no chroma)
      gl.useProgram(this.chromaProgram);
      gl.bindVertexArray(this.quadVAO);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, this.fboB!.texture);
      gl.uniform1i(this.getUniform(this.chromaProgram!, "u_texture"), 0);
      gl.uniform1f(this.getUniform(this.chromaProgram!, "u_offset"), 0.0);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    }
    // quality === "low": no post-processing drawn

    // Check for GL errors — if any, mark context as broken to avoid error cascade
    const err = gl.getError();
    if (err !== gl.NO_ERROR) {
      console.warn(
        `[WebGL2] GL error ${err} after render — disabling pipeline`,
      );
      this.contextLost = true;
      this.initialized = false;
    }
  }

  private setupShaders(): void {
    if (!this.gl) return;
    const gl = this.gl;

    this.bloomExtractProgram = this.createProgram(
      gl,
      VERTEX_SHADER,
      BLOOM_EXTRACT_FRAG,
    );
    this.bloomBlurProgram = this.createProgram(
      gl,
      VERTEX_SHADER,
      BLOOM_BLUR_FRAG,
    );
    this.bloomCompositeProgram = this.createProgram(
      gl,
      VERTEX_SHADER,
      BLOOM_COMPOSITE_FRAG,
    );
    this.chromaProgram = this.createProgram(gl, VERTEX_SHADER, CHROMA_FRAGMENT);
    this.sourceTexture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, this.sourceTexture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  }

  private setupGeometry(): void {
    if (!this.gl) return;
    const gl = this.gl;

    // Fullscreen quad: pos(x,y) + texCoord(s,t)
    const vertices = new Float32Array([
      -1, -1, 0, 0, 1, -1, 1, 0, -1, 1, 0, 1, 1, 1, 1, 1,
    ]);

    this.quadVAO = gl.createVertexArray();
    gl.bindVertexArray(this.quadVAO);

    const vbo = gl.createBuffer();
    this.quadVBO = vbo;
    gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
    gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);

    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 16, 0);
    gl.enableVertexAttribArray(1);
    gl.vertexAttribPointer(1, 2, gl.FLOAT, false, 16, 8);

    gl.bindVertexArray(null);
  }

  /** M5-F4: Cache uniform locations to avoid per-frame string lookups on GPU driver */
  private getUniform(
    prog: WebGLProgram,
    name: string,
  ): WebGLUniformLocation | null {
    let progMap = this.uniformCache.get(prog);
    if (!progMap) {
      progMap = new Map();
      this.uniformCache.set(prog, progMap);
    }
    if (progMap.has(name)) return progMap.get(name)!;
    const loc = this.gl!.getUniformLocation(prog, name);
    progMap.set(name, loc);
    return loc;
  }

  private updateSourceTexture(source: HTMLCanvasElement): void {
    if (!this.gl || !this.sourceTexture) return;
    const gl = this.gl;
    gl.bindTexture(gl.TEXTURE_2D, this.sourceTexture);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
    // Allocate storage once (texImage2D); on subsequent frames the canvas size
    // is stable, so texSubImage2D updates the pixels in place without
    // re-specifying — avoids a per-frame GPU storage realloc.
    if (source.width !== this.sourceTexW || source.height !== this.sourceTexH) {
      gl.texImage2D(
        gl.TEXTURE_2D,
        0,
        gl.RGBA,
        gl.RGBA,
        gl.UNSIGNED_BYTE,
        source,
      );
      this.sourceTexW = source.width;
      this.sourceTexH = source.height;
    } else {
      gl.texSubImage2D(
        gl.TEXTURE_2D,
        0,
        0,
        0,
        gl.RGBA,
        gl.UNSIGNED_BYTE,
        source,
      );
    }
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
  }

  private ensureFBOs(w: number, h: number): void {
    if (!this.gl) return;
    if (this.fboA && this.fboWidth === w && this.fboHeight === h) return;
    this.destroyFBOs();
    this.fboA = this.createFBO(w, h); // half-res for bloom blur
    this.fboB = this.createFBO(w, h); // half-res for blur ping-pong (resized to full for composite)
    this.fboWidth = w;
    this.fboHeight = h;
  }

  private createFBO(w: number, h: number): FBO | null {
    const gl = this.gl!;
    const texture = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texImage2D(
      gl.TEXTURE_2D,
      0,
      gl.RGBA,
      w,
      h,
      0,
      gl.RGBA,
      gl.UNSIGNED_BYTE,
      null,
    );
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

    const framebuffer = gl.createFramebuffer()!;
    gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer);
    gl.framebufferTexture2D(
      gl.FRAMEBUFFER,
      gl.COLOR_ATTACHMENT0,
      gl.TEXTURE_2D,
      texture,
      0,
    );
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);

    return { framebuffer, texture, width: w, height: h };
  }

  private destroyFBOs(): void {
    if (!this.gl) return;
    const gl = this.gl;
    if (this.fboA) {
      gl.deleteFramebuffer(this.fboA.framebuffer);
      gl.deleteTexture(this.fboA.texture);
      this.fboA = null;
    }
    if (this.fboB) {
      gl.deleteFramebuffer(this.fboB.framebuffer);
      gl.deleteTexture(this.fboB.texture);
      this.fboB = null;
    }
    this.fboWidth = 0;
    this.fboHeight = 0;
  }

  private renderToFBO(
    fbo: FBO,
    program: WebGLProgram | null,
    bindUniforms: (prog: WebGLProgram) => void,
    w: number,
    h: number,
  ): void {
    if (!this.gl || !program || !this.quadVAO) return;
    const gl = this.gl;

    // Resize FBO texture if needed
    if (fbo.width !== w || fbo.height !== h) {
      gl.bindTexture(gl.TEXTURE_2D, fbo.texture);
      gl.texImage2D(
        gl.TEXTURE_2D,
        0,
        gl.RGBA,
        w,
        h,
        0,
        gl.RGBA,
        gl.UNSIGNED_BYTE,
        null,
      );
      fbo.width = w;
      fbo.height = h;
    }

    gl.bindFramebuffer(gl.FRAMEBUFFER, fbo.framebuffer);
    gl.viewport(0, 0, w, h);
    gl.useProgram(program);
    gl.bindVertexArray(this.quadVAO);
    bindUniforms(program);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  }

  private createProgram(
    gl: WebGL2RenderingContext,
    vertSrc: string,
    fragSrc: string,
  ): WebGLProgram | null {
    const vert = this.compileShader(gl, gl.VERTEX_SHADER, vertSrc);
    const frag = this.compileShader(gl, gl.FRAGMENT_SHADER, fragSrc);
    if (!vert || !frag) return null;

    const program = gl.createProgram()!;
    gl.attachShader(program, vert);
    gl.attachShader(program, frag);
    gl.linkProgram(program);

    // M5-F14: Delete shaders after linking to free GPU memory
    gl.detachShader(program, vert);
    gl.detachShader(program, frag);
    gl.deleteShader(vert);
    gl.deleteShader(frag);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      console.error("Program link error:", gl.getProgramInfoLog(program));
      return null;
    }
    return program;
  }

  private compileShader(
    gl: WebGL2RenderingContext,
    type: number,
    source: string,
  ): WebGLShader | null {
    const shader = gl.createShader(type)!;
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      console.error("Shader compile error:", gl.getShaderInfoLog(shader));
      gl.deleteShader(shader);
      return null;
    }
    return shader;
  }
}
